import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import IconMC from '../IconMC';
import openIntercom from '../../utils/openIntercom';

const TABS = {
  CONTACT: 'contact',
  SELF_HELP: 'selfHelp',
  RESOURCES: 'resources'
};

/**
 * HelpCenterBarMC component renders a fixed bottom bar with a help center panel
 * that opens upward, inspired by Bigin's design.
 * Uses a portal to render directly in the body, avoiding overflow issues.
 */
const HelpCenterBarMC = () => {
  const { t } = useTranslation('HelpCenter');
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(
    window.ENV?.HIDE_SUPPORT_CHAT ? TABS.SELF_HELP : TABS.CONTACT
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [hasErbBar, setHasErbBar] = useState(false);
  const panelRef = useRef(null);
  const hideSupportChat = window.ENV?.HIDE_SUPPORT_CHAT;

  // Check if ERB help bar already exists (to avoid duplicate bars)
  useEffect(() => {
    const erbBar = document.querySelector('.help-center-bar-erb');
    setHasErbBar(!!erbBar);
  }, []);

  // Detect mobile screen size (debounced to avoid excessive re-renders)
  useEffect(() => {
    let timeoutId;
    const checkMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 1024);
      }, 200);
    };
    
    setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(timeoutId);
    };
  }, []);

  const tabs = [
    ...(!hideSupportChat ? [{ id: TABS.CONTACT, label: t('tabs.contact') }] : []),
    { id: TABS.SELF_HELP, label: t('tabs.selfHelp') },
    { id: TABS.RESOURCES, label: t('tabs.resources') }
  ];

  // Listen for Intercom unread messages count (skip when support chat is hidden)
  useEffect(() => {
    if (hideSupportChat) return;

    let intervalId;
    
    const setupIntercomListener = () => {
      if (window.Intercom) {
        window.Intercom('onUnreadCountChange', (count) => {
          setUnreadCount(count);
        });
        
        if (intervalId) {
          clearInterval(intervalId);
        }
        return true;
      }
      return false;
    };

    if (!setupIntercomListener()) {
      intervalId = setInterval(() => {
        if (setupIntercomListener()) {
          clearInterval(intervalId);
        }
      }, 500);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const handleOpenIntercom = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    openIntercom();
    setIsOpen(false);
  }, []);

  const openFrillRoadmap = useCallback(() => {
    setIsOpen(false);
    
    const frillButton = document.querySelector('.frill-roadmap');
    if (frillButton) {
      frillButton.click();
      return;
    }
    
    if (window.Frill_Instance) {
      window.Frill_Instance.open();
      return;
    }
    
    const frillTrigger = document.querySelector('[data-frill-widget]') || 
                         document.querySelector('.frill-widget-trigger') ||
                         document.querySelector('[class*="frill"]');
    if (frillTrigger) {
      frillTrigger.click();
    }
  }, []);

  const copyEmailToClipboard = useCallback(() => {
    navigator.clipboard.writeText('soporte@mercately.com').then(() => {
      if (window.showtoast) {
        window.showtoast(t('contact.email.copied'));
      } else {
        alert(t('contact.email.copied'));
      }
    });
  }, [t]);

  const contactOptions = useMemo(() => [
    {
      iconName: 'help-headphones',
      iconBgColor: 'tw-bg-blue-50',
      iconColor: 'tw-text-blue-500',
      title: t('contact.chat.title'),
      description: t('contact.chat.description'),
      onClick: handleOpenIntercom,
      hasArrow: true,
      badge: unreadCount > 0 ? unreadCount : null
    },
    {
      iconName: 'help-email',
      iconBgColor: 'tw-bg-amber-50',
      iconColor: 'tw-text-amber-600',
      title: t('contact.email.title'),
      description: 'soporte@mercately.com',
      onClick: copyEmailToClipboard,
      hasArrow: true
    }
  ], [t, unreadCount, handleOpenIntercom, copyEmailToClipboard]);

  const selfHelpOptions = useMemo(() => [
    {
      iconName: 'help-video',
      iconBgColor: 'tw-bg-pink-50',
      iconColor: 'tw-text-pink-500',
      title: t('selfHelp.academy.title'),
      description: t('selfHelp.academy.description'),
      href: `${window.location.origin}/retailers/${window.ENV?.SLUG}/academy`,
      external: false,
      hasArrow: true
    },
    {
      iconName: 'help-book',
      iconBgColor: 'tw-bg-yellow-50',
      iconColor: 'tw-text-yellow-600',
      title: t('selfHelp.knowledgeBase.title'),
      description: t('selfHelp.knowledgeBase.description'),
      href: 'https://support.mercately.com',
      external: true,
      hasArrow: false
    }
  ], [t]);

  const resourcesOptions = useMemo(() => [
    {
      iconName: 'help-blog',
      iconBgColor: 'tw-bg-emerald-50',
      iconColor: 'tw-text-emerald-500',
      title: t('resources.blog.title'),
      description: t('resources.blog.description'),
      href: 'https://blog.mercately.com',
      external: true,
      hasArrow: false
    },
    {
      iconName: 'help-roadmap',
      iconBgColor: 'tw-bg-purple-50',
      iconColor: 'tw-text-purple-500',
      title: t('resources.roadmap.title'),
      description: t('resources.roadmap.description'),
      onClick: openFrillRoadmap,
      hasArrow: true
    }
  ], [t, openFrillRoadmap]);

  const renderOption = (option, index) => {
    const content = (
      <div 
        className="tw-flex tw-items-center tw-p-3 tw-rounded-lg tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors"
        onClick={option.onClick}
      >
        <div className={`tw-relative tw-w-10 tw-h-10 tw-rounded-full ${option.iconBgColor} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
          <IconMC name={option.iconName} size="lg" className={option.iconColor} />
          {option.badge && (
            <span className="tw-absolute -tw-top-1 -tw-right-1 tw-bg-red-500 tw-text-white tw-text-xs tw-font-bold tw-rounded-full tw-min-w-[18px] tw-h-[18px] tw-flex tw-items-center tw-justify-center tw-px-1">
              {option.badge > 9 ? '9+' : option.badge}
            </span>
          )}
        </div>
        <div className="tw-flex-1 tw-ml-3 tw-min-w-0">
          <h3 className="tw-font-medium tw-text-gray-900 tw-text-sm">{option.title}</h3>
          <p className="tw-text-gray-500 tw-text-xs tw-truncate">{option.description}</p>
        </div>
        {option.hasArrow && (
          <IconMC name="chevron-right-outline" size="base" className="tw-text-gray-400 tw-flex-shrink-0" />
        )}
        {option.external && (
          <IconMC name="external-link-outline" size="sm" className="tw-text-blue-500 tw-flex-shrink-0" />
        )}
      </div>
    );

    if (option.href) {
      return (
        <a 
          key={index}
          href={option.href}
          target={option.external ? '_blank' : '_self'}
          rel={option.external ? 'noopener noreferrer' : undefined}
          className="tw-no-underline tw-block"
          onClick={() => !option.external && setIsOpen(false)}
        >
          {content}
        </a>
      );
    }

    return <div key={index}>{content}</div>;
  };

  const getOptionsForTab = () => {
    switch (activeTab) {
      case TABS.CONTACT:
        return contactOptions;
      case TABS.SELF_HELP:
        return selfHelpOptions;
      case TABS.RESOURCES:
        return resourcesOptions;
      default:
        return contactOptions;
    }
  };

  // Don't render on mobile or if ERB bar already exists
  if (isMobile || hasErbBar) {
    return null;
  }

  // Render using portal to body to avoid overflow issues
  return createPortal(
    <div 
      ref={panelRef}
      className="tw-wrapper"
      data-help-center-bar
      style={{
        position: 'fixed',
        bottom: 0,
        left: '60px', // Account for sidebar
        right: 0,
        zIndex: 40 // Lower than modals and datepickers so it stays behind popups
      }}
    >
      {/* Panel - only rendered when open to reduce DOM nodes */}
      {isOpen && (
        <div 
          className="tw-absolute tw-right-4 tw-w-[420px] tw-bg-white tw-rounded-t-2xl tw-shadow-2xl tw-border tw-border-b-0 tw-border-gray-200 tw-overflow-hidden"
          style={{ bottom: '100%' }}
        >
          {/* Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-pt-5 tw-pb-3">
            <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">
              {t('title')}
            </h2>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="tw-p-1.5 tw-rounded-full hover:tw-bg-gray-100 tw-transition-colors"
            >
              <IconMC name="close-outline" size="lg" className="tw-text-gray-400" />
            </button>
          </div>

          {/* Tabs */}
          <div className="tw-px-5 tw-border-b tw-border-gray-100">
            <div className="tw-flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`tw-flex-1 tw-px-3 tw-py-3 tw-text-sm tw-font-medium tw-transition-colors tw-relative tw-text-center
                    ${activeTab === tab.id 
                      ? 'tw-text-blue-600' 
                      : 'tw-text-gray-500 hover:tw-text-gray-700'
                    }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="tw-absolute tw-bottom-0 tw-left-2 tw-right-2 tw-h-0.5 tw-bg-blue-500 tw-rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="tw-p-4 tw-min-h-[400px] tw-max-h-[600px] tw-overflow-y-auto">
            <div className="tw-space-y-2">
              {getOptionsForTab().map((option, index) => renderOption(option, index))}
            </div>
          </div>
        </div>
      )}

      {/* Full-width bottom bar - compact */}
      <div 
        className={`tw-flex tw-items-center tw-justify-between tw-px-3 tw-transition-colors tw-duration-200 ${
          isOpen 
            ? '' 
            : 'tw-bg-white tw-border-t tw-border-gray-200'
        }`}
        style={{ 
          paddingTop: '8px', 
          paddingBottom: '8px',
          backgroundColor: isOpen ? '#3CAAE1' : undefined
        }}
      >
        {/* Left side - branding or info */}
        <div className={`tw-flex tw-items-center tw-gap-1.5 ${isOpen ? 'tw-text-white' : 'tw-text-gray-600'}`}>
          <svg 
            className="tw-w-3.5 tw-h-3.5" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span className="tw-font-medium tw-text-xs">{t('footer.subtitle')}</span>
        </div>

        {/* Right side - trigger button and Intercom launcher */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            onClick={() => {
              // Close Intercom if it's open when opening the help panel
              if (!isOpen && window.Intercom) {
                window.Intercom('hide');
              }
              setIsOpen(!isOpen);
            }}
            className={`tw-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded tw-transition-all tw-duration-200 ${
              isOpen 
                ? 'tw-bg-white/20 tw-text-white hover:tw-bg-white/30' 
                : 'tw-text-white'
            }`}
            style={{
              backgroundColor: isOpen ? undefined : '#3CAAE1'
            }}
            onMouseEnter={(e) => { if (!isOpen) e.currentTarget.style.backgroundColor = '#2B99D0'; }}
            onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.backgroundColor = '#3CAAE1'; }}
          >
            <span className="tw-font-medium tw-text-xs">{t('triggerButton')}</span>
            <svg 
              className={`tw-w-3 tw-h-3 tw-transition-transform tw-duration-200 ${isOpen ? 'tw-rotate-180' : ''}`}
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
          
          {!hideSupportChat && (
            <button
              type="button"
              onClick={handleOpenIntercom}
              className={`tw-relative tw-rounded-full tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-shadow-md hover:tw-scale-110 tw-transition-all tw-duration-200 active:tw-scale-90 ${
                isOpen 
                  ? 'tw-bg-white/20 hover:tw-bg-white/30' 
                  : ''
              }`}
              style={{ 
                width: '1.5rem', 
                height: '1.5rem',
                backgroundColor: isOpen ? undefined : '#3CAAE1' 
              }}
              aria-label="Open Intercom Messenger"
            >
              <img 
                src="https://downloads.intercomcdn.com/i/o/579882/565aa792c5960765f3b97714/cb41407a9c2975e5f2501d38a529e394.png" 
                alt="Chat" 
                style={{ width: '1rem', height: '1rem' }}
              />
              {unreadCount > 0 && (
                <span className="tw-absolute -tw-top-1 -tw-right-1 tw-bg-red-500 tw-text-white tw-text-[10px] tw-font-bold tw-rounded-full tw-min-w-[16px] tw-h-[16px] tw-flex tw-items-center tw-justify-center tw-px-1">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default HelpCenterBarMC;
